import { formatTime, tt } from '@agos/shared'

/**
 * Where an order has got to — the one question this page is opened to ask.
 *
 * `GET /customer-auth/my-orders/{id}/tracking` answers the order and a **timeline** from
 * `order_status_logs`: every status it has been in, with the moment it changed. Legacy polls it every
 * **15 seconds** while the order is still moving and stops when it is not, and §14.1 is explicit that
 * the interval is copied rather than judged.
 *
 * A C0 spec recorded this endpoint as *"a fifteenth legacy never calls"* and filed live tracking as a
 * §9 improvement for after parity. That was wrong — `MojProfil.tsx` polls it — and the mistake was
 * invisible because the inventory extractor stopped reading a URL at its first `${…}`, so the whole
 * sub-path behind the order id was missing from the comparison. Both are corrected.
 *
 * **Polling stops when the order stops.** A delivered order is not going to change, and a page that
 * keeps asking is a page that keeps waking a phone.
 */

/** Statuses an order can still move out of. Legacy's set, exactly. */
const TRACKABLE = new Set(['new', 'preparing', 'ready', 'on_the_way'])

export interface TrackingStep {
  status: string
  at: string
}

export interface Tracking {
  orderId: number | null
  orderNumber: string
  status: string
  timeline: TrackingStep[]
}

export function trackingOf(payload: unknown): Tracking | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as { order?: unknown; timeline?: unknown }
  if (typeof row.order !== 'object' || row.order === null) return null
  const order = row.order as Record<string, unknown>
  const status = typeof order.status === 'string' ? order.status : ''
  if (status === '') return null

  const rawTimeline = Array.isArray(row.timeline) ? row.timeline : []
  const timeline: TrackingStep[] = []
  for (const entry of rawTimeline) {
    if (typeof entry !== 'object' || entry === null) continue
    const step = entry as Record<string, unknown>
    // Both spellings: the row comes from the database and the mapper camel-cases the order but not
    // always the log.
    const changedAt =
      typeof step.changedAt === 'string'
        ? step.changedAt
        : typeof step.changed_at === 'string'
          ? step.changed_at
          : ''
    if (typeof step.status !== 'string' || step.status === '' || changedAt === '') continue
    timeline.push({ status: step.status, at: changedAt })
  }

  return {
    orderId: typeof order.id === 'number' ? order.id : null,
    orderNumber: typeof order.orderNumber === 'string' ? order.orderNumber : '',
    status,
    timeline,
  }
}

/**
 * Whether to ask again.
 *
 * `null` — no order being tracked — is not the same as a finished one, and both stop the poll; the
 * difference is that one of them can start it.
 */
export function stillMoving(tracking: Tracking | null): boolean {
  return tracking !== null && TRACKABLE.has(tracking.status)
}

/** How often to ask, in milliseconds. Legacy's interval, copied (§14.1). */
export const TRACKING_INTERVAL_MS = 15_000

/** The poll interval TanStack should use, or `false` once the order has stopped moving. */
export function trackingInterval(tracking: Tracking | null): number | false {
  return stillMoving(tracking) ? TRACKING_INTERVAL_MS : false
}

/** `Nová · 18:42` — a step in the customer's own words, with the moment it happened. */
export function stepNote(step: TrackingStep, label: (status: string) => string): string {
  return `${label(step.status)} · ${formatTime(step.at)}`
}

/** What to say when an order has a status but no timeline behind it. */
export function emptyTimelineNote(): string {
  // The logs only start when a status *changes*, so a brand-new order genuinely has none — saying the
  // table is empty would read as something being broken.
  return tt('Zatiaľ bez ďalších zmien.')
}
